import React, { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const SHEET_WIDTH = 1220;
const SHEET_LENGTH = 3000;

interface FlashingConfigurationFormProps {
  initialVariation: 'plastisol' | 'polyester' | 'galvanised_steel';
  initialQuantity: number;
  initialWeltStart: boolean;
  initialWeltEnd: boolean;
  initialPlastisolPrice: number;
  initialPolyesterPrice: number;
  initialGalvanisedSteelPrice: number;
  initialType?: 'internal' | 'external';
  initialColor?: string;
  initialGauge?: '0.5mm' | '0.7mm' | '1.5mm';
  totalWidth: number;
  segmentsLength: number;
  readOnlyPrices?: boolean;
  onSave: (config: {
    variation: 'plastisol' | 'polyester' | 'galvanised_steel';
    quantity: number;
    weltStart: boolean;
    weltEnd: boolean;
    plastisolPrice: number;
    polyesterPrice: number;
    galvanisedSteelPrice: number;
    type: 'internal' | 'external';
    color?: string;
    gauge?: '0.5mm' | '0.7mm' | '1.5mm';
  }) => void;
}

export interface FlashingConfigurationFormRef {
  getValues: () => {
    variation: 'plastisol' | 'polyester' | 'galvanised_steel';
    quantity: number;
    weltStart: boolean;
    weltEnd: boolean;
    plastisolPrice: number;
    polyesterPrice: number;
    galvanisedSteelPrice: number;
    type: 'internal' | 'external';
    color?: string;
    gauge?: '0.5mm' | '0.7mm' | '1.5mm';
  };
}

export const FlashingConfigurationForm = forwardRef<FlashingConfigurationFormRef, FlashingConfigurationFormProps>(({ 
  initialVariation,
  initialQuantity,
  initialWeltStart,
  initialWeltEnd,
  initialPlastisolPrice,
  initialPolyesterPrice,
  initialGalvanisedSteelPrice,
  initialType = 'external',
  initialColor = '',
  initialGauge = '0.5mm',
  totalWidth,
  segmentsLength,
  readOnlyPrices = false,
  onSave
}, ref) => {
  // Use refs for inputs to avoid re-renders
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const plastisolPriceInputRef = useRef<HTMLInputElement>(null);
  const polyesterPriceInputRef = useRef<HTMLInputElement>(null);
  const galvanisedSteelPriceInputRef = useRef<HTMLInputElement>(null);
  
  // State for controlled components
  const [localVariation, setLocalVariation] = useState<'plastisol' | 'polyester' | 'galvanised_steel'>(initialVariation);
  const [localWeltStart, setLocalWeltStart] = useState(initialWeltStart);
  const [localWeltEnd, setLocalWeltEnd] = useState(initialWeltEnd);
  const [localType, setLocalType] = useState<'internal' | 'external'>(initialType);
  const [localColor, setLocalColor] = useState<string>(initialColor);
  const [localGauge, setLocalGauge] = useState<'0.5mm' | '0.7mm' | '1.5mm'>(initialGauge);
  
  // Initialize input values only once
  const [initialized, setInitialized] = useState(false);
  
  // Reset to 0.5mm when switching away from galvanised_steel if current gauge is 1.5mm
  // (since 1.5mm is only available for galvanised_steel)
  useEffect(() => {
    if (localVariation !== 'galvanised_steel' && localGauge === '1.5mm') {
      // Reset to 0.5mm when switching away from galvanised_steel
      setLocalGauge('0.5mm');
    }
  }, [localVariation, localGauge]);

  // Clear color when galvanised_steel is selected
  useEffect(() => {
    if (localVariation === 'galvanised_steel' && localColor) {
      setLocalColor('');
    }
  }, [localVariation, localColor]);
  
  useEffect(() => {
    if (!initialized) {
      if (readOnlyPrices) {
        // When prices are read-only, only initialize the quantity input
        if (quantityInputRef.current) {
          quantityInputRef.current.value = String(initialQuantity);
          setInitialized(true);
        }
      } else if (quantityInputRef.current && plastisolPriceInputRef.current && polyesterPriceInputRef.current && galvanisedSteelPriceInputRef.current) {
        quantityInputRef.current.value = String(initialQuantity);
        plastisolPriceInputRef.current.value = String(initialPlastisolPrice);
        polyesterPriceInputRef.current.value = String(initialPolyesterPrice);
        galvanisedSteelPriceInputRef.current.value = String(initialGalvanisedSteelPrice);
        setInitialized(true);
      }
    }
  }, [initialized, initialQuantity, initialPlastisolPrice, initialPolyesterPrice, initialGalvanisedSteelPrice, readOnlyPrices]);

  const localTotalWidth = useMemo(() => {
    const weltWidth = (localWeltStart ? 15 : 0) + (localWeltEnd ? 15 : 0);
    const baseWidth = totalWidth - (initialWeltStart ? 15 : 0) - (initialWeltEnd ? 15 : 0);
    return baseWidth + weltWidth;
  }, [totalWidth, localWeltStart, localWeltEnd, initialWeltStart, initialWeltEnd]);

  // Calculate units per sheet (accounting for offcuts) - same as sidebar
  const unitsPerSheet = useMemo(() => {
    if (localTotalWidth === 0) return 0;
    return Math.floor(SHEET_WIDTH / localTotalWidth);
  }, [localTotalWidth]);

  // Calculate price per unit (sheet price divided by units per sheet) - same as sidebar
  const pricePerUnit = useMemo(() => {
    if (unitsPerSheet === 0) return 0;
    let pricePerSheet = 0;
    if (localVariation === 'plastisol') {
      pricePerSheet = plastisolPriceInputRef.current ? parseFloat(plastisolPriceInputRef.current.value) || 0 : initialPlastisolPrice;
    } else if (localVariation === 'polyester') {
      pricePerSheet = polyesterPriceInputRef.current ? parseFloat(polyesterPriceInputRef.current.value) || 0 : initialPolyesterPrice;
    } else if (localVariation === 'galvanised_steel') {
      pricePerSheet = galvanisedSteelPriceInputRef.current ? parseFloat(galvanisedSteelPriceInputRef.current.value) || 0 : initialGalvanisedSteelPrice;
    }
    return pricePerSheet / unitsPerSheet;
  }, [unitsPerSheet, localVariation, initialPlastisolPrice, initialPolyesterPrice, initialGalvanisedSteelPrice]);

  const calculatePrice = useCallback(() => {
    if (unitsPerSheet === 0) return 0;
    const qty = quantityInputRef.current ? parseInt(quantityInputRef.current.value, 10) || 1 : 1;
    return pricePerUnit * qty;
  }, [pricePerUnit, unitsPerSheet]);

  // Expose getValues function via ref
  useImperativeHandle(ref, () => ({
    getValues: () => ({
      variation: localVariation,
      quantity: quantityInputRef.current ? parseInt(quantityInputRef.current.value, 10) || 1 : 1,
      weltStart: localWeltStart,
      weltEnd: localWeltEnd,
      plastisolPrice: plastisolPriceInputRef.current ? parseFloat(plastisolPriceInputRef.current.value) || 0 : initialPlastisolPrice,
      polyesterPrice: polyesterPriceInputRef.current ? parseFloat(polyesterPriceInputRef.current.value) || 0 : initialPolyesterPrice,
      galvanisedSteelPrice: galvanisedSteelPriceInputRef.current ? parseFloat(galvanisedSteelPriceInputRef.current.value) || 0 : initialGalvanisedSteelPrice,
      type: localType,
      color: localColor || undefined,
      gauge: localGauge
    })
  }));

  // Get current values for display
  const getQuantity = () => quantityInputRef.current?.value || String(initialQuantity);
  const getPlastisolPrice = () => readOnlyPrices ? String(initialPlastisolPrice) : (plastisolPriceInputRef.current?.value || String(initialPlastisolPrice));
  const getPolyesterPrice = () => readOnlyPrices ? String(initialPolyesterPrice) : (polyesterPriceInputRef.current?.value || String(initialPolyesterPrice));
  const getGalvanisedSteelPrice = () => readOnlyPrices ? String(initialGalvanisedSteelPrice) : (galvanisedSteelPriceInputRef.current?.value || String(initialGalvanisedSteelPrice));

  return (
    <div className="space-y-6 pb-4">
      {/* Total Width Info */}
      <section>
        <p className="text-xs text-muted-foreground">
          Total width: <span className="font-semibold">{localTotalWidth}mm</span>
        </p>
      </section>

      {/* Configuration */}
      <section className="border-t pt-4">
        <Label className="text-base font-semibold">Configuration</Label>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select 
              value={localType} 
              onValueChange={(v: 'internal' | 'external') => {
                setLocalType(v);
              }}
            >
              <SelectTrigger className="w-full" data-form-field>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="z-[10001]" data-radix-select-content>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="external">External</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Variation</Label>
            <Select 
              value={localVariation} 
              onValueChange={(v: 'plastisol' | 'polyester' | 'galvanised_steel') => {
                setLocalVariation(v);
                // Clear color when galvanised_steel is selected
                if (v === 'galvanised_steel') {
                  setLocalColor('');
                }
              }}
            >
              <SelectTrigger className="w-full" data-form-field>
                <SelectValue placeholder="Select variation" />
              </SelectTrigger>
              <SelectContent className="z-[10001]" data-radix-select-content>
                <SelectItem value="plastisol">Plastisol</SelectItem>
                <SelectItem value="polyester">Polyester</SelectItem>
                <SelectItem value="galvanised_steel">Galvanised Steel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {localVariation !== 'galvanised_steel' && (
            <div className="space-y-2">
              <Label>Colour</Label>
              <Select 
                value={localColor || '__none__'} 
                onValueChange={(v: string) => {
                  setLocalColor(v === '__none__' ? '' : v);
                }}
              >
                <SelectTrigger className="w-full" data-form-field>
                  <SelectValue placeholder="Select colour" />
                </SelectTrigger>
                <SelectContent className="z-[10001]" data-radix-select-content>
                  <SelectItem value="__none__">None</SelectItem>
                  <SelectItem value="Slate Grey">Slate Grey</SelectItem>
                  <SelectItem value="Vandyke Brown">Vandyke Brown</SelectItem>
                  <SelectItem value="White">White</SelectItem>
                  <SelectItem value="Wedgewood Blue">Wedgewood Blue</SelectItem>
                  <SelectItem value="Olive Green">Olive Green</SelectItem>
                  <SelectItem value="Ocean Blue">Ocean Blue</SelectItem>
                  <SelectItem value="Terracotta">Terracotta</SelectItem>
                  <SelectItem value="Moorland Green">Moorland Green</SelectItem>
                  <SelectItem value="Merlin Grey">Merlin Grey</SelectItem>
                  <SelectItem value="Juniper Green">Juniper Green</SelectItem>
                  <SelectItem value="Goosewing Grey">Goosewing Grey</SelectItem>
                  <SelectItem value="Black">Black</SelectItem>
                  <SelectItem value="Anthracite">Anthracite</SelectItem>
                  <SelectItem value="Plain Galvanised">Plain Galvanised</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Gauge</Label>
            <Select 
              value={localGauge} 
              onValueChange={(v: '0.5mm' | '0.7mm' | '1.5mm') => {
                setLocalGauge(v);
              }}
            >
              <SelectTrigger className="w-full" data-form-field>
                <SelectValue placeholder="Select gauge" />
              </SelectTrigger>
              <SelectContent className="z-[10001]" data-radix-select-content>
                <SelectItem value="0.5mm">0.5mm</SelectItem>
                <SelectItem value="0.7mm">0.7mm</SelectItem>
                {localVariation === 'galvanised_steel' && (
                  <SelectItem value="1.5mm">1.5mm</SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Gauge selection does not affect pricing
            </p>
          </div>

          <div className="space-y-2">
            <Label>Quantity</Label>
            <Input
              ref={quantityInputRef}
              type="number"
              min="1"
              defaultValue={initialQuantity}
              key={`quantity-${initialQuantity}`}
            />
          </div>

          {/* Welt Options */}
          <div className="space-y-3 border-t pt-3">
            <Label className="text-sm font-semibold">Welt Options</Label>
            <p className="text-xs text-muted-foreground">
              15mm folded over at ends
            </p>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 w-full text-left hover:bg-muted/50 rounded-md p-3 transition-colors cursor-pointer group">
                <input
                  type="checkbox"
                  checked={localWeltStart}
                  onChange={(e) => {
                    setLocalWeltStart(e.target.checked);
                  }}
                  className="h-5 w-5 rounded-sm border-2 border-primary text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                />
                <span className="text-sm font-normal select-none flex-1">
                  Welt at Start (15mm)
                </span>
              </label>
              <label className="flex items-center space-x-3 w-full text-left hover:bg-muted/50 rounded-md p-3 transition-colors cursor-pointer group">
                <input
                  type="checkbox"
                  checked={localWeltEnd}
                  onChange={(e) => {
                    setLocalWeltEnd(e.target.checked);
                  }}
                  className="h-5 w-5 rounded-sm border-2 border-primary text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                />
                <span className="text-sm font-normal select-none flex-1">
                  Welt at End (15mm)
                </span>
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Configuration */}
      <section className="border-t pt-4">
        <Label className="text-base font-semibold">Pricing</Label>
        <p className="text-xs text-muted-foreground mt-1">
          Price per sheet ({SHEET_WIDTH}mm width × {SHEET_LENGTH}mm length / 3m) - <span className="font-semibold">excluding VAT</span>
        </p>
        <div className="space-y-3 mt-2">
          <div className="space-y-2">
            <Label className="text-sm">Plastisol Price (per sheet) <span className="text-xs font-normal text-muted-foreground">(ex VAT)</span></Label>
            {readOnlyPrices ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                <span className="text-sm font-semibold">£{initialPlastisolPrice.toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  ref={plastisolPriceInputRef}
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={initialPlastisolPrice}
                  key={`plastisol-${initialPlastisolPrice}`}
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Polyester Price (per sheet) <span className="text-xs font-normal text-muted-foreground">(ex VAT)</span></Label>
            {readOnlyPrices ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                <span className="text-sm font-semibold">£{initialPolyesterPrice.toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  ref={polyesterPriceInputRef}
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={initialPolyesterPrice}
                  key={`polyester-${initialPolyesterPrice}`}
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Galvanised Steel Price (per sheet) <span className="text-xs font-normal text-muted-foreground">(ex VAT)</span></Label>
            {readOnlyPrices ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                <span className="text-sm font-semibold">£{initialGalvanisedSteelPrice.toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  ref={galvanisedSteelPriceInputRef}
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={initialGalvanisedSteelPrice}
                  key={`galvanised-steel-${initialGalvanisedSteelPrice}`}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Summary - Calculations */}
      <section className="border-t pt-4">
        <Label className="text-base font-semibold">Calculations</Label>
        <div className="bg-muted/50 rounded-lg p-4 space-y-3 mt-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Width:</span>
            <span className="font-semibold">{localTotalWidth}mm</span>
          </div>
          {(localWeltStart || localWeltEnd) && (
            <div className="text-xs text-muted-foreground pl-2 border-l-2 border-primary/20 space-y-1">
              <div className="flex justify-between">
                <span>Base width:</span>
                <span>{localTotalWidth - (localWeltStart ? 15 : 0) - (localWeltEnd ? 15 : 0)}mm</span>
              </div>
              {localWeltStart && (
                <div className="flex justify-between">
                  <span>Welt at start:</span>
                  <span>+15mm</span>
                </div>
              )}
              {localWeltEnd && (
                <div className="flex justify-between">
                  <span>Welt at end:</span>
                  <span>+15mm</span>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Sheet Size:</span>
            <span className="font-semibold">{SHEET_WIDTH}mm × {SHEET_LENGTH}mm</span>
          </div>
          {localTotalWidth > 0 && (
            <>
              {unitsPerSheet > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Units per Sheet:</span>
                    <span className="font-semibold">{unitsPerSheet}</span>
                  </div>
                  {(SHEET_WIDTH - (unitsPerSheet * localTotalWidth)) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Offcut per Sheet:</span>
                      <span className="font-semibold text-orange-600">{(SHEET_WIDTH - (unitsPerSheet * localTotalWidth)).toFixed(1)}mm</span>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground italic pl-2 border-l-2 border-primary/20">
                    Calculation: {SHEET_WIDTH}mm ÷ {localTotalWidth}mm = {unitsPerSheet} units
                    {(SHEET_WIDTH - (unitsPerSheet * localTotalWidth)) > 0 && ` (${(SHEET_WIDTH - (unitsPerSheet * localTotalWidth)).toFixed(1)}mm offcut)`}
                  </div>
                </>
              )}
            </>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Variation:</span>
            <span className="font-semibold capitalize">{localVariation}</span>
          </div>
          {unitsPerSheet > 0 && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Price per Unit:</span>
                <span className="font-semibold">
                  £{pricePerUnit.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span>
                </span>
              </div>
              <div className="text-xs text-muted-foreground italic pl-2 border-l-2 border-primary/20">
                Calculation: £{(() => {
                  let price = 0;
                  if (localVariation === 'plastisol') price = parseFloat(getPlastisolPrice()) || 0;
                  else if (localVariation === 'polyester') price = parseFloat(getPolyesterPrice()) || 0;
                  else if (localVariation === 'galvanised_steel') price = parseFloat(getGalvanisedSteelPrice()) || 0;
                  return price.toFixed(2);
                })()} ÷ {unitsPerSheet} = £{pricePerUnit.toFixed(2)} (ex VAT)
              </div>
            </>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Quantity:</span>
            <span className="font-semibold">{getQuantity()}</span>
          </div>
          <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
            <span>Total Price:</span>
            <span className="text-primary">£{calculatePrice().toFixed(2)} <span className="text-sm font-normal text-muted-foreground">ex VAT</span></span>
          </div>
        </div>
      </section>
    </div>
  );
});

FlashingConfigurationForm.displayName = 'FlashingConfigurationForm';
